import { DURABLE_PILOT_ID, PILOT_PAGE_LIMIT, eventConversationId, pilotContractError, validatePilotSnapshot } from "./durablePilotContract";

/** One actor-scoped, memory-only recovery session. No management endpoints. */
export function createDurablePilotCoordinator({ snapshot, replay, history, compatibility, dispatch, getState, onChange }) {
  let enabled = false, disposed = false, visible = false, active = false, stopped = false;
  let generation = 0, cursor = null, flight = null, historyFlight = null, timer = null;
  let pending = false, needsSnapshot = true, retryAt = 0, failures = 0;
  let snapshotAgain = false;
  let view = { enabled: false, mode: "off", busy: false, error: null, snapshot: null };
  const publish = patch => { view = { ...view, ...patch }; if (!disposed) onChange(view); };
  const clearTimer = () => { clearTimeout(timer); timer = null; };
  const schedule = delay => {
    clearTimer();
    if (disposed || stopped || !enabled) return;
    timer = setTimeout(() => { timer = null; wake(); }, delay);
  };
  const current = token => !disposed && enabled && generation === token;
  const fail = error => {
    const terminal = [401, 403].includes(error.status) || ["MESSAGING_RECOVERY_EVENT_INVALID", "MESSAGING_CHANNEL_UNAVAILABLE", "MESSAGING_LOCAL_HISTORY_UNAVAILABLE", "MESSAGING_PILOT_UNAVAILABLE"].includes(error.code);
    stopped = terminal;
    publish({ error, busy: false, mode: view.mode === "loading" ? "unavailable" : view.mode });
    if (!terminal) {
      retryAt = Date.now() + Math.min(30000, 1000 * (2 ** Math.min(failures++, 5)));
      schedule(retryAt - Date.now());
    }
  };
  async function takeSnapshot(token) {
    const result = await snapshot(DURABLE_PILOT_ID);
    if (!current(token)) return;
    validatePilotSnapshot(result);
    if (result.recovery.state === "prepared") {
      if (active) {
        // Keep durable ownership until BOTH compatibility reads succeed.
        const restored = await compatibility(DURABLE_PILOT_ID);
        if (!current(token)) return;
        dispatch({ type: "messaging/pilot-rollback", ...restored });
      }
      active = false;
      cursor = null;
      pending = false;
      publish({ mode: "prepared", snapshot: result, error: null });
    } else {
      dispatch({ type: "messaging/pilot-snapshot", snapshot: result });
      active = true;
      cursor = result.recovery.cursor;
      publish({ mode: "active", snapshot: result, error: null });
    }
    needsSnapshot = false;
    failures = 0;
  }
  async function run(token) {
    if (!current(token)) return;
    publish({ busy: true });
    try {
      if (needsSnapshot) await takeSnapshot(token);
      if (!current(token) || !active) return;
      do {
        pending = false;
        const previousCursor = cursor;
        const result = await replay(DURABLE_PILOT_ID, previousCursor);
        if (!current(token)) return;
        if (result?.scope !== "conversation" || !Array.isArray(result.items)
          || typeof result.page?.next_cursor !== "string" || !result.page.next_cursor
          || typeof result.page.has_more !== "boolean"
          || (result.page.has_more && result.page.next_cursor === previousCursor)) throw pilotContractError("Invalid replay page; checkpoint retained.");
        // dispatch reduces synchronously; a rejected page never advances cursor.
        dispatch({ type: "messaging/pilot-replay", items: result.items, previousCursor, cursor: result.page.next_cursor });
        cursor = result.page.next_cursor;
        failures = 0;
        publish({ error: null });
        if (result.page.waiting_for_publication) {
          retryAt = Date.now() + Math.max(100, Number(result.page.retry_after_ms) || 1000);
          schedule(retryAt - Date.now());
          return;
        }
        if (result.page.has_more) pending = true;
      } while (pending && current(token));
    } catch (error) {
      if (!current(token)) return;
      if (error.status === 409 && error.code === "MESSAGING_RECOVERY_RESET_REQUIRED" && error.details?.reload_snapshot) {
        generation += 1;
        cursor = null;
        needsSnapshot = true;
        pending = true;
        retryAt = Date.now() + 100; // also bounds repeated reset failures
      } else fail(error);
    } finally {
      if (!disposed) publish({ busy: false });
    }
  }
  function wake() {
    if (!enabled || disposed || stopped) return Promise.resolve(false);
    if (flight) { pending = true; return flight; }
    if (Date.now() < retryAt) { schedule(retryAt - Date.now()); return Promise.resolve(false); }
    if (!active && !needsSnapshot) return Promise.resolve(true);
    clearTimer();
    flight = Promise.resolve().then(() => run(generation)).finally(() => {
      flight = null;
      if (disposed || stopped) return;
      if (snapshotAgain && !active) { needsSnapshot = true; pending = true; }
      snapshotAgain = false;
      if (pending || needsSnapshot || retryAt > Date.now()) schedule(Math.max(0, retryAt - Date.now()));
      else if (active && visible) schedule(5000);
    });
    return flight;
  }
  function refresh() {
    if (!enabled || disposed) return Promise.resolve(false);
    generation += 1;
    needsSnapshot = true;
    stopped = false;
    retryAt = 0;
    pending = true;
    return wake();
  }
  async function loadOlder() {
    if (!active) return false;
    if (historyFlight) return historyFlight;
    const token = generation;
    const scope = getState().apiHydration.messageScopes[DURABLE_PILOT_ID];
    if (!scope?.has_more || !scope.next_cursor) return false;
    historyFlight = (async () => {
      try {
        let result, append = true;
        try {
          result = await history(DURABLE_PILOT_ID, { read_source: "local", limit: PILOT_PAGE_LIMIT, cursor: scope.next_cursor });
        } catch (error) {
          if (error.code !== "MESSAGING_CURSOR_STALE" || !error.details?.restart_from_first_page) throw error;
          if (!current(token)) return false;
          result = await history(DURABLE_PILOT_ID, { read_source: "local", limit: PILOT_PAGE_LIMIT });
          append = false;
        }
        if (!current(token) || !active) return false;
        if (!Array.isArray(result?.items) || result.items.some(item => item?.conversation_id !== DURABLE_PILOT_ID)
          || result.page?.pagination_source !== "local_store") throw pilotContractError("Invalid local history page; current messages retained.");
        dispatch({ type: "messaging/pilot-history", items: result.items, page: { ...result.page, history: result.history }, append });
        publish({ error: null });
        return true;
      } catch (error) { if (current(token)) fail(error); return false; }
      finally { historyFlight = null; }
    })();
    return historyFlight;
  }
  return {
    enable() { if (!enabled) { enabled = true; publish({ enabled: true, mode: "loading" }); } return refresh(); },
    refresh, wake, loadOlder,
    isEnabled: () => enabled,
    isActive: () => active,
    getView: () => view,
    setVisible(value, { reopen = true } = {}) {
      visible = value;
      if (visible && enabled) { if (!active && reopen) refresh(); else if (active) wake(); }
      else if (!value && retryAt <= Date.now()) clearTimer();
    },
    handleSocket(event) {
      if (!enabled || event?.durability !== "durable" || eventConversationId(event, getState()) !== DURABLE_PILOT_ID) return false;
      if (active) wake();
      else if (!flight) refresh();
      else snapshotAgain = true;
      return true;
    },
    dispose() { disposed = true; enabled = false; generation += 1; clearTimer(); },
  };
}
